import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './CookieConsent.css';

export default function CookieConsent() {
  const location = useLocation();
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consentChoice = localStorage.getItem('velox-cookie-consent');
    
    // Define public routes where banner should appear
    const publicRoutes = ['/', '/about', '/contact', '/features', '/pricing', '/privacy', '/terms', '/resources', '/waitlist'];
    const isPublicPage = publicRoutes.includes(location.pathname);
    
    // Only show banner on public pages when user is not logged in and hasn't made a choice
    if (!user && isPublicPage && !consentChoice) {
      setShowBanner(true);
    } else {
      setShowBanner(false);
    }
  }, [location.pathname, user]);

  // Sync consent state with CookieHub when component mounts
  useEffect(() => {
    const consentChoice = localStorage.getItem('velox-cookie-consent');
    if (consentChoice) {
      // Sync our consent choice with CookieHub's expected format
      localStorage.setItem('cookiehub-consent', consentChoice === 'accepted' ? 'true' : 'false');
      localStorage.setItem('ch2-consent', consentChoice === 'accepted' ? 'true' : 'false');
      
      // If CookieHub is already loaded, update its state
      if (window.cookiehub && typeof window.cookiehub.setConsent === 'function') {
        window.cookiehub.setConsent(consentChoice === 'accepted');
      }
      if (window.ch2 && typeof window.ch2.setConsent === 'function') {
        window.ch2.setConsent(consentChoice === 'accepted');
      }
    }
  }, []);

  const handleAccept = () => {
    const consent = 'accepted';
    localStorage.setItem('velox-cookie-consent', consent);
    
    // Sync with CookieHub's consent format
    localStorage.setItem('cookiehub-consent', 'true');
    localStorage.setItem('ch2-consent', 'true');
    
    // Update CookieHub if it's loaded
    if (window.cookiehub && typeof window.cookiehub.setConsent === 'function') {
      window.cookiehub.setConsent(true);
    }
    if (window.ch2 && typeof window.ch2.setConsent === 'function') {
      window.ch2.setConsent(true);
    }
    
    // Push to dataLayer for GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'cookie_consent',
        consent_status: 'accepted'
      });
    }
    
    setShowBanner(false);
  };

  const handleReject = () => {
    const consent = 'rejected';
    localStorage.setItem('velox-cookie-consent', consent);
    
    // Sync with CookieHub's consent format
    localStorage.setItem('cookiehub-consent', 'false');
    localStorage.setItem('ch2-consent', 'false');
    
    // Update CookieHub if it's loaded
    if (window.cookiehub && typeof window.cookiehub.setConsent === 'function') {
      window.cookiehub.setConsent(false);
    }
    if (window.ch2 && typeof window.ch2.setConsent === 'function') {
      window.ch2.setConsent(false);
    }
    
    // Push to dataLayer for GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'cookie_consent',
        consent_status: 'rejected'
      });
    }
    
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="cookie-consent-banner">
      <div className="cookie-consent-content">
        <p className="cookie-consent-text">
          We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts. By clicking "Accept", you consent to our use of cookies.{' '}
          <Link to="/privacy" className="cookie-consent-link">
            Learn more
          </Link>
        </p>
        <div className="cookie-consent-buttons">
          <button 
            className="cookie-consent-button cookie-consent-button-reject"
            onClick={handleReject}
          >
            Reject
          </button>
          <button 
            className="cookie-consent-button cookie-consent-button-accept"
            onClick={handleAccept}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
